import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, fmtDate } from "@/lib/api";
import { useSession } from "@/context/Session";
import ReviewQueue from "@/pages/ReviewQueue";
import { StatusBadge } from "@/components/Badges";
import { pick } from "@/components/Translate";
import { t } from "@/i18n";
import { Tt } from "@/components/Tt";
import { OiTopics } from "@/components/OiTopics";
import { CouponAlerts } from "@/components/CouponAlerts";

const Group = ({ title, tone, items, render, testid }: any) => (
  <div data-testid={testid}>
    <p className="mono-label flex items-center gap-2 mb-2"><span className="w-2 h-2 rounded-full" style={{ background: `var(--status-${tone})` }} />{t(title)} · {items.length}</p>
    <div className="card !p-0 divide-y divide-[var(--border-color)]">
      {items.length === 0 ? <p className="text-sm faint p-4">{t("Nothing here.")}</p> : items.map(render)}
    </div>
  </div>
);

function MyReview() {
  const api = useApi();
  const [qs, setQs] = useState<any[]>([]);
  const [subs, setSubs] = useState<any[]>([]);
  useEffect(() => {
    api.get("/questions/mine").then((r) => setQs(r.data.filter((q: any) => q.routed)));
    api.get("/sops/mine").then((r) => setSubs(r.data.map((s: any) => (s.rejected ? { ...s, status: "rejected" } : s))));
  }, [api]);
  const waiting = qs.filter((q) => q.status === "open");
  const replied = qs.filter((q) => q.owner_reply).reverse();
  const row = (q: any) => (
    <div key={q.id} className="p-4" data-testid={`my-review-question-${q.id}`}>
      <p className="font-medium">{pick(q, "question", false)}</p>
      {q.owner_reply ? <p className="text-sm mt-1"><span className="text-[var(--status-ok)] font-semibold">{t("Owner")}:</span> {pick(q, "owner_reply", false)}</p> : <p className="text-xs faint mt-1">{t("Asked")} {fmtDate(q.created_at)}</p>}
    </div>
  );
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 stack">
      <h1 className="text-3xl font-bold">{t("Review")}</h1>
      <Group title="Waiting on owner" tone="error" items={waiting} render={row} testid="my-review-waiting" />
      <Group title="Owner replied" tone="ok" items={replied} render={row} testid="my-review-replied" />
      <Group title="My submissions" tone="info" items={subs} testid="my-review-submissions" render={(s: any) => (
        <Link key={s.id} to={`/sops/${s.id}`} className="flex items-center justify-between gap-3 p-4 hover:bg-[var(--bg-surface-hover)] transition-colors" data-testid={`my-review-sub-${s.id}`}>
          <span className="min-w-0"><span className="block font-medium truncate"><Tt text={s.title} /></span>
            {s.rejected && s.reject_reason && <span className="block text-xs muted mt-0.5">{t("Owner")}: “{s.reject_reason}”</span>}</span>
          <StatusBadge status={s.status} />
        </Link>
      )} />
    </div>
  );
}

export default function Review() {
  const { me } = useSession();
  return me.is_maintainer
    ? <><div className="max-w-3xl mx-auto px-4 pt-6"><CouponAlerts /></div><ReviewQueue /><OiTopics /></>
    : <><div className="max-w-3xl mx-auto px-4 pt-6"><CouponAlerts /></div><MyReview /><OiTopics /></>;
}
